import { lazy, Suspense, useRef, useState } from 'react';

import type { Nullable } from '@repo/common';
import { getErrorMessage } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSText } from '@repo/sds/SDSText';
import type { CropMeta, CropperRef } from '@repo/ui/components/elements/Cropper/Cropper';
import { FileInputWithDragAndDrop } from '@repo/ui/components/fileInputs/FileInputWithDragAndDrop/FileInputWithDragAndDrop';
import { IMAGE_UPLOAD_ACCEPT, IMAGE_UPLOAD_MAX_FILE_SIZE_BYTES } from '@repo/ui/constants/image-upload';

// Lazy load Cropper component (341 KB gzipped - only load when modal opens)
const Cropper = lazy(() =>
  import('@repo/ui/components/elements/Cropper/Cropper').then(module => ({
    default: module.Cropper,
  })),
);

type AspectRatio = 'square' | '16:9';

interface ImageCropperModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (thumbnailFile: File, meta: CropMeta) => Promise<void>;
  imageUrl: Nullable<string>;
  initialMeta?: CropMeta;
  aspectRatio: AspectRatio;
  onReplace?: (file: File) => void | Promise<void>;
  onReplaceError?: (message: string) => void;
  errorMessage?: string | null;
  isDisabled?: boolean;
}

const aspectRatioValues: Record<AspectRatio, number> = {
  square: 1,
  '16:9': 16 / 9,
};

export function ImageCropperModal({
  isOpen,
  onClose,
  onSave,
  imageUrl,
  initialMeta,
  aspectRatio,
  onReplace,
  onReplaceError,
  errorMessage,
  isDisabled = false,
}: ImageCropperModalProps) {
  const sessionKey = isOpen ? `open:${imageUrl ?? 'missing'}` : 'closed';
  return (
    <ImageCropperModalContent
      key={sessionKey}
      isOpen={isOpen}
      onClose={onClose}
      onSave={onSave}
      imageUrl={imageUrl}
      initialMeta={initialMeta}
      aspectRatio={aspectRatio}
      onReplace={onReplace}
      onReplaceError={onReplaceError}
      errorMessage={errorMessage}
      isDisabled={isDisabled}
    />
  );
}

function ImageCropperModalContent({
  isOpen,
  onClose,
  onSave,
  imageUrl,
  initialMeta,
  aspectRatio,
  onReplace,
  onReplaceError,
  errorMessage,
  isDisabled = false,
}: ImageCropperModalProps) {
  const cropperRef = useRef<CropperRef>(null);
  const [cropperReady, setCropperReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const savingRef = useRef(false);

  const handleSave = async () => {
    if (savingRef.current || isDisabled) return;
    const cropper = cropperRef.current;

    if (!(cropper && cropperReady)) {
      // Cropper ref not available
      return;
    }

    savingRef.current = true;
    setSaving(true);
    setSaveError(null);

    // React Compiler can't lower a `throw` inside a try/catch, so failures
    // here report through this helper and an early return instead.
    const fail = (message: string): void => {
      setSaveError(message);
      savingRef.current = false;
      setSaving(false);
    };

    let thumbnailFile: File | null;
    try {
      thumbnailFile = await cropper.toFile();
    } catch (error) {
      fail(getErrorMessage(error));
      return;
    }
    if (!(thumbnailFile instanceof File)) {
      fail('Could not generate the cropped image. Please try again.');
      return;
    }

    let meta: CropMeta;
    try {
      meta = { ...cropper.getMetaData(), aspect: aspectRatioValues[aspectRatio] };
    } catch (error) {
      fail(getErrorMessage(error));
      return;
    }
    if (![meta.x, meta.y, meta.aspect, meta.rotate, meta.scale].every(Number.isFinite)) {
      fail('The image crop is invalid. Please adjust it and try again.');
      return;
    }

    try {
      await onSave(thumbnailFile, meta);
    } catch (error) {
      fail(getErrorMessage(error));
      return;
    }
    savingRef.current = false;
    setSaving(false);
  };

  return (
    <SDSDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Image"
      primaryAction={{
        label: 'Save',
        onClick: handleSave,
        loading: saving,
        disabled: !saving && (!cropperReady || isDisabled),
        dataTestId: DataTestIds.MODALS.IMAGE_UPLOAD.SAVE_BTN,
      }}
      secondaryAction={{
        label: 'Cancel',
        onClick: onClose,
        dataTestId: DataTestIds.MODALS.IMAGE_UPLOAD.CANCEL_BTN,
      }}
    >
      {imageUrl && (
        <Suspense fallback={<SDSText type="body-medium" text="Loading image editor..." />}>
          <Cropper
            key={`${imageUrl}:${aspectRatio}`}
            ref={cropperRef}
            src={imageUrl}
            aspect={aspectRatioValues[aspectRatio]}
            initialMeta={initialMeta}
            onReadyChange={setCropperReady}
          />
        </Suspense>
      )}
      {onReplace && (
        <FileInputWithDragAndDrop
          accept={IMAGE_UPLOAD_ACCEPT}
          inputAriaLabel="Replace image"
          onChange={onReplace}
          onError={onReplaceError}
          maxFileSizeBytes={IMAGE_UPLOAD_MAX_FILE_SIZE_BYTES}
          isDisabled={saving || isDisabled}
          button={{ text: 'Replace image', mode: 'outline' }}
        />
      )}
      {!imageUrl && (
        <SDSText type="body-large" text="Something went wrong!" className="ImageCropperModal__error-text" />
      )}
      {saveError || errorMessage ? (
        <div role="alert" aria-live="assertive" className="ImageCropperModal__error-text">
          {saveError ?? errorMessage}
        </div>
      ) : null}
    </SDSDialog>
  );
}
